import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Plus, X, Copy, UserPlus, ShieldCheck, Trash2, Mail, Truck, Save } from "lucide-react";

const emptyDriver = { name: "", email: "", phone: "", license_number: "", assigned_vehicle_id: "", password: "" };
const emptyVehicle = { registration: "", make_model: "", capacity_kg: "", active: true, notes: "" };

export default function Drivers() {
  const { user } = useAuth();
  const [tab, setTab] = useState("drivers");
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState(emptyDriver);
  const [busy, setBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);

  const load = async () => {
    const [d, v] = await Promise.all([api.get("/drivers"), api.get("/vehicles")]);
    setDrivers(d.data);
    setVehicles(v.data);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);
  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const vehiclesById = Object.fromEntries(vehicles.map((v) => [v.id, v]));
  const activeVehicles = vehicles.filter((v) => v.active);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/drivers", {
        ...form,
        assigned_vehicle_id: form.assigned_vehicle_id || null,
      });
      setInviteResult(data);
      toast.success("Driver invite sent");
      setForm(emptyDriver);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  const removeDriver = async (d) => {
    if (!window.confirm(`Deactivate ${d.name}? Their history stays intact.`)) return;
    await api.delete(`/drivers/${d.user_id}`);
    toast.success("Deactivated"); load();
  };

  const saveDriverEdit = async () => {
    try {
      await api.patch(`/drivers/${editingDriver.user_id}`, {
        name: editingDriver.name,
        phone: editingDriver.phone,
        license_number: editingDriver.license_number,
        assigned_vehicle_id: editingDriver.assigned_vehicle_id || null,
        active: editingDriver.active,
      });
      toast.success("Saved");
      setEditingDriver(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const saveVehicle = async () => {
    const payload = {
      ...editingVehicle,
      capacity_kg: editingVehicle.capacity_kg === "" ? null : Number(editingVehicle.capacity_kg),
    };
    try {
      if (editingVehicle.id) {
        await api.put(`/vehicles/${editingVehicle.id}`, payload);
      } else {
        await api.post("/vehicles", payload);
      }
      toast.success("Saved");
      setEditingVehicle(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const removeVehicle = async (v) => {
    if (!window.confirm(`Deactivate ${v.registration}?`)) return;
    await api.delete(`/vehicles/${v.id}`);
    toast.success("Deactivated"); load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Team"
        title="Drivers & Fleet"
        subtitle="Add every driver and truck so trips can be assigned to real people, not free-text names."
        actions={
          tab === "drivers" ? (
            <button onClick={() => { setInviting(true); setInviteResult(null); setForm(emptyDriver); }} className="btn-primary inline-flex items-center gap-2" data-testid="invite-driver-btn">
              <UserPlus className="w-4 h-4" /> Invite driver
            </button>
          ) : (
            <button onClick={() => setEditingVehicle({ ...emptyVehicle })} className="btn-primary inline-flex items-center gap-2" data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4" /> Add vehicle
            </button>
          )
        }
      />
      <div className="px-8 lg:px-12 py-8 space-y-6">
        <div className="flex gap-1 border-b" style={{ borderColor: "var(--border)" }}>
          <button onClick={() => setTab("drivers")} className={`px-4 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "drivers" ? "font-semibold" : "text-neutral-500"}`} style={{ borderColor: tab === "drivers" ? "var(--forest)" : "transparent" }} data-testid="tab-drivers">
            <UserPlus className="w-4 h-4" /> Drivers · {drivers.filter((d) => d.active).length}
          </button>
          <button onClick={() => setTab("vehicles")} className={`px-4 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "vehicles" ? "font-semibold" : "text-neutral-500"}`} style={{ borderColor: tab === "vehicles" ? "var(--forest)" : "transparent" }} data-testid="tab-vehicles">
            <Truck className="w-4 h-4" /> Vehicles · {vehicles.filter((v) => v.active).length}
          </button>
        </div>

        {tab === "drivers" && (
          <div className="hbt-card overflow-hidden">
            <table className="hbt-table w-full">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Phone</th><th>Licence</th><th>Assigned truck</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {drivers.map((d) => (
                  <tr key={d.user_id} data-testid={`driver-row-${d.user_id}`} style={{ opacity: d.active ? 1 : 0.5 }}>
                    <td className="font-medium">{d.name}</td>
                    <td className="text-neutral-600 text-sm">{d.email}</td>
                    <td className="text-neutral-600 text-sm font-mono-num">{d.phone || "—"}</td>
                    <td className="text-neutral-600 text-sm font-mono-num">{d.license_number || "—"}</td>
                    <td className="text-neutral-600 text-sm">{vehiclesById[d.assigned_vehicle_id]?.registration || "—"}</td>
                    <td>
                      {!d.has_password ? (
                        <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: "rgba(200,140,0,0.14)", color: "#8a6900" }}>{d.invite_status || "pending"}</span>
                      ) : d.active ? (
                        <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: "rgba(26,54,45,0.08)", color: "var(--forest)" }}>active</span>
                      ) : (
                        <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: "rgba(120,120,120,0.15)", color: "#666" }}>inactive</span>
                      )}
                    </td>
                    <td className="text-right">
                      <button onClick={() => setEditingDriver({ ...d })} className="text-xs text-neutral-500 hover:text-black mr-3" data-testid={`edit-driver-${d.user_id}`}>Edit</button>
                      {d.active && <button onClick={() => removeDriver(d)} className="text-xs text-neutral-500 hover:text-red-700" data-testid={`del-driver-${d.user_id}`}><Trash2 className="w-4 h-4 inline" /></button>}
                    </td>
                  </tr>
                ))}
                {drivers.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-neutral-500">No drivers yet — invite your first teammate above.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "vehicles" && (
          <div className="hbt-card overflow-hidden">
            <table className="hbt-table w-full">
              <thead>
                <tr><th>Registration</th><th>Make / Model</th><th>Capacity (kg)</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} data-testid={`vehicle-row-${v.id}`} style={{ opacity: v.active ? 1 : 0.5 }}>
                    <td className="font-mono-num font-medium">{v.registration}</td>
                    <td className="text-neutral-600 text-sm">{v.make_model || "—"}</td>
                    <td className="font-mono-num text-neutral-600 text-sm">{v.capacity_kg ? v.capacity_kg.toLocaleString() : "—"}</td>
                    <td className="text-neutral-600 text-sm">{v.active ? "Active" : "Inactive"}</td>
                    <td className="text-right">
                      <button onClick={() => setEditingVehicle({ ...v, capacity_kg: v.capacity_kg ?? "" })} className="text-xs text-neutral-500 hover:text-black mr-3" data-testid={`edit-vehicle-${v.id}`}>Edit</button>
                      {v.active && <button onClick={() => removeVehicle(v)} className="text-xs text-neutral-500 hover:text-red-700" data-testid={`del-vehicle-${v.id}`}><Trash2 className="w-4 h-4 inline" /></button>}
                    </td>
                  </tr>
                ))}
                {vehicles.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-neutral-500">No trucks yet — add your first vehicle above.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite modal */}
      {inviting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setInviting(false)}>
          <div className="hbt-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="driver-invite-modal">
            {inviteResult ? (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5" style={{ color: "var(--forest)" }} />
                  <div className="font-display text-xl font-semibold">Driver added</div>
                </div>
                {inviteResult.mode === "direct_password" ? (
                  <>
                    <p className="text-sm text-neutral-600 mb-4">Share these credentials with the driver in person. They&apos;ll be asked to change the password on first login.</p>
                    <div className="hbt-input font-mono text-sm bg-neutral-50" data-testid="driver-creds-display">
                      Login: <b>{form.email || "(see above)"}</b><br />
                      Password: <b>{form.password}</b>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-neutral-600 mb-4">Share this URL over WhatsApp if the email doesn&apos;t land — link is valid for 48 hours:</p>
                    <div className="hbt-input font-mono text-xs break-all bg-neutral-50" data-testid="driver-invite-url">{inviteResult.invite_url}</div>
                  </>
                )}
                <div className="flex justify-end gap-2 mt-5">
                  <button onClick={() => { setInviting(false); setInviteResult(null); }} className="btn-primary" data-testid="close-driver-invite">Done</button>
                </div>
              </div>
            ) : (
              <form onSubmit={sendInvite}>
                <div className="font-display text-xl font-semibold mb-1">Add a driver</div>
                <p className="text-xs text-neutral-500 mb-5">Drivers don&apos;t need a real email — pick a unique login ID and password, and hand them over in person.</p>
                <div className="space-y-3">
                  <div>
                    <label className="hbt-label block mb-2">Full name</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="hbt-input" data-testid="driver-name-input" />
                  </div>
                  <div>
                    <label className="hbt-label block mb-2">Login ID (any unique email — no invite email is sent)</label>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="hbt-input" placeholder="bongani.mabuza@hbtwaste.local" data-testid="driver-email-input" />
                  </div>
                  <div>
                    <label className="hbt-label block mb-2">Temporary password (min 6 chars) — hand it to them in person</label>
                    <input type="text" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="hbt-input font-mono" placeholder="e.g. HBT2026#" data-testid="driver-password-input" />
                    <div className="text-xs text-neutral-500 mt-1">They&apos;ll be prompted to change it on first login.</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="hbt-label block mb-2">Phone</label>
                      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="hbt-input" placeholder="+268 7…" data-testid="driver-phone-input" />
                    </div>
                    <div>
                      <label className="hbt-label block mb-2">Driving licence</label>
                      <input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="hbt-input" placeholder="SZ-P-…" data-testid="driver-license-input" />
                    </div>
                  </div>
                  <div>
                    <label className="hbt-label block mb-2">Assigned truck (optional)</label>
                    <select value={form.assigned_vehicle_id} onChange={(e) => setForm({ ...form, assigned_vehicle_id: e.target.value })} className="hbt-input" data-testid="driver-vehicle-select">
                      <option value="">— None —</option>
                      {activeVehicles.map((v) => <option key={v.id} value={v.id}>{v.registration}{v.make_model ? ` · ${v.make_model}` : ""}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button type="button" onClick={() => setInviting(false)} className="btn-outline" disabled={busy}>Cancel</button>
                  <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={busy} data-testid="send-driver-invite">
                    <Mail className="w-4 h-4" /> {busy ? "Adding…" : "Add driver"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Edit driver */}
      {editingDriver && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingDriver(null)}>
          <div className="hbt-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="edit-driver-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl font-semibold">Edit driver</div>
              <button onClick={() => setEditingDriver(null)}><X className="w-5 h-5 text-neutral-400" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="hbt-label block mb-2">Full name</label><input className="hbt-input" value={editingDriver.name || ""} onChange={(e) => setEditingDriver({ ...editingDriver, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="hbt-label block mb-2">Phone</label><input className="hbt-input" value={editingDriver.phone || ""} onChange={(e) => setEditingDriver({ ...editingDriver, phone: e.target.value })} /></div>
                <div><label className="hbt-label block mb-2">Driving licence</label><input className="hbt-input" value={editingDriver.license_number || ""} onChange={(e) => setEditingDriver({ ...editingDriver, license_number: e.target.value })} /></div>
              </div>
              <div>
                <label className="hbt-label block mb-2">Assigned truck</label>
                <select className="hbt-input" value={editingDriver.assigned_vehicle_id || ""} onChange={(e) => setEditingDriver({ ...editingDriver, assigned_vehicle_id: e.target.value })}>
                  <option value="">— None —</option>
                  {activeVehicles.map((v) => <option key={v.id} value={v.id}>{v.registration}{v.make_model ? ` · ${v.make_model}` : ""}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-600"><input type="checkbox" checked={editingDriver.active !== false} onChange={(e) => setEditingDriver({ ...editingDriver, active: e.target.checked })} /> Active</label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditingDriver(null)} className="btn-outline">Cancel</button>
              <button onClick={saveDriverEdit} className="btn-primary inline-flex items-center gap-2"><Save className="w-4 h-4" />Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle editor */}
      {editingVehicle && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingVehicle(null)}>
          <div className="hbt-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="vehicle-editor">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl font-semibold">{editingVehicle.id ? "Edit vehicle" : "New vehicle"}</div>
              <button onClick={() => setEditingVehicle(null)}><X className="w-5 h-5 text-neutral-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="hbt-label block mb-2">Registration</label>
                <input required className="hbt-input font-mono-num" value={editingVehicle.registration} onChange={(e) => setEditingVehicle({ ...editingVehicle, registration: e.target.value })} placeholder="HBT-01" data-testid="vehicle-reg-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="hbt-label block mb-2">Make / Model</label><input className="hbt-input" value={editingVehicle.make_model || ""} onChange={(e) => setEditingVehicle({ ...editingVehicle, make_model: e.target.value })} placeholder="Isuzu NPR" /></div>
                <div><label className="hbt-label block mb-2">Capacity (kg)</label><input type="number" min="0" className="hbt-input font-mono-num" value={editingVehicle.capacity_kg} onChange={(e) => setEditingVehicle({ ...editingVehicle, capacity_kg: e.target.value })} placeholder="5000" /></div>
              </div>
              <div><label className="hbt-label block mb-2">Notes</label><textarea rows={2} className="hbt-input" value={editingVehicle.notes || ""} onChange={(e) => setEditingVehicle({ ...editingVehicle, notes: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm text-neutral-600"><input type="checkbox" checked={editingVehicle.active !== false} onChange={(e) => setEditingVehicle({ ...editingVehicle, active: e.target.checked })} /> Active</label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditingVehicle(null)} className="btn-outline">Cancel</button>
              <button onClick={saveVehicle} className="btn-primary inline-flex items-center gap-2" data-testid="save-vehicle-btn"><Save className="w-4 h-4" />Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
